import R from "ramda";
import moment from "moment";
import { store } from "store";
import {
  batchAssignCategories,
  fetchDocuments,
  mergeDocuments,
  putDocument,
  trashStatusMultipleDocuments,
  deleteDocumentsPermanently,
  selectAllDocuments,
  getShareUrl,
  fetchVendorRules
} from "api/documents";
import {
  batchDownloadsSharedAction,
  documentViewedActionGenerator,
  filterActionGenerator,
  paginationActionGenerator,
  submitBatchEditActionGenerator
} from "actions/shared/shared-action-generators";
import { trackEventAction } from "actions/shared/tracking-actions";
import { setFlashMessageAction } from "actions/app-actions";
import { storage, VISIBLE_DOCUMENTS_COLUMNS } from "utils/local-storage";
import { filterCounter } from "utils/filter-counter";
import { isShiftPressed } from "utils/key-press";
import { columnObjects } from "components/grid/column-objects";

export const SET_DOCUMENTS = "actions/documents/set-documents";
export const LOADING_START = "actions/documents/loading-start";
export const SET_PAGINATION_LIMIT = "actions/documents/pagination-limit";
export const NEXT_PAGE = "actions/documents/next-page";
export const PREVIOUS_PAGE = "actions/documents/previous-page";
export const TOGGLE_COLUMN = "actions/documents/toggle-column";
export const SET_EDIT_COLUMNS_VISIBILITY = "actions/documents/set-edit-columns-visibility";
export const SELECT_DOCUMENTS = "actions/documents/select-documents";
export const DESELECT_DOCUMENTS = "actions/documents/deselect-documents";
export const START_BATCH_EDIT_MODE = "actions/documents/start-batch-edit-mode";
export const END_BATCH_EDIT_MODE = "actions/documents/end-batch-edit-mode";
export const START_BATCH_CATEGORIES_MODE = "actions/documents/start-batch-categories-mode";
export const END_BATCH_CATEGORIES_MODE = "action/documents/end-batch-categories-mode";
export const SET_DOCUMENT_RULES = "actions/documents/set-document-rules";

export const BATCH_EDIT_LOADING = "actions/documents/batch-edit-loading";
export const BATCH_EDIT_LOADING_STOP = "actions/documents/batch-edit-loading-stop";

export const BATCH_ASSIGN_CATEGORIES_LOADING = "actions/documents/start-batch-assign-categories-loading";
export const BATCH_ASSIGN_CATEGORIES_LOADING_STOP = "actions/documents/batch-assign-categories-loading-stop";

export const START_BATCH_DOWNLOAD_MODE = "actions/documents/start-batch-download-mode";
export const START_BATCH_DOWNLOAD_LOADING = "actions/documents/start-batch-download-loading";
export const END_BATCH_DOWNLOAD_MODE = "actions/documents/end-batch-download-mode";
export const SET_TRASH_DOCUMENT_VISIBILITY = "actions/documents/set-trash-document-visibility";
export const SET_CHANGE_DOCUMENT_TYPE_VISIBILITY = "actions/documents/set-change-document-type-visibility";
export const SET_SHARE_URL_VISIBILITY = "actions/documents/set-share-url-visibility";
export const DOCUMENT_VIEWED = "actions/documents/document-viewed";

export const MERGE_DOCUMENTS_LOADING = "actions/documents/merge-documents-loading";
export const MERGE_DOCUMENTS_LOADING_STOP = "actions/documents/merge-documents-loading-stop";
export const START_MERGE_DOCUMENTS_MODE = "actions/documents/start-merge-documents-mode";
export const END_MERGE_DOCUMENTS_MODE = "actions/documents/end-merge-documents-mode";

export const SET_TRASH_MULTIPLE_DOCUMENTS_VISIBILITY = "actions/documents/set-trash-multiple-documents-visibility";
export const TRASH_MULTIPLE_DOCUMENTS_LOADING = "actions/documents/trash-multiple-documents-loading";
export const TRASH_MULTIPLE_DOCUMENTS_LOADING_STOP = "actions/documents/trash-multiple-documents-loading-stop";
export const START_DRAGGABILITY = "actions/documents/start-draggability";
export const END_DRAGGABILITY = "actions/documents/end-draggability";

export const START_HAMBURGER_MODE = "actions/documents/start-hamburger-mode";
export const END_HAMBURGER_MODE = "actions/documents/end-hamburger-mode";
export const START_DOC_VIEWER_HAMBURGER_MODE = "actions/documents/start-doc-viewer-hamburger-mode";
export const END_DOC_VIEWER_HAMBURGER_MODE = "actions/documents/end-doc-viewer-hamburger-mode";
export const START_FILTERS_DRAWER_MODE = "actions/documents/start-filters-drawer-mode";
export const END_FILTERS_DRAWER_MODE = "actions/documents/end-filters-drawer-mode";
export const RESTORE_DOCUMENTS_LOADING = "actions/documents/restore-documents-loading";
export const RESTORE_DOCUMENTS_LOADING_STOP = "actions/documents/restore-documents-loading-stop";
export const DELETE_DOCUMENTS_PERMANENTLY_LOADING = "actions/documents/delete-documents-permanently-loading";
export const DELETE_DOCUMENTS_PERMANENTLY_LOADING_STOP = "actions/documents/delete-documents-permanently-loading-stop";
export const SET_DELETE_PERMANENTLY_VISIBILITY = "actions/documents/set-delete-permanently-visibility";
export const DESELECT_ALL_DOCUMENTS = "actions/documents/deselect-all-documents";
export const SELECT_ALL_DOCUMENTS = "actions/documents/select-all-documents";
export const SELECT_MERGE_DOCUMENT = "actions/documents/select-merge-document";
export const DESELECT_MERGE_DOCUMENT = "actions/documents/deselect-merge-document";
export const SELECT_MERGE_FIELD = "actions/documents/select-merge-field";
export const SET_DOCUMENT_VIEWER_VISIBILITY = "actions/documents/set-document-viewer-visibility";
export const SHARE_URL_GENERATED = "actions/documents/share-url-generated";
export const SHOW_GL_CODES_PANEL = "actions/documents/show-gl-codes-panel";
export const HIDE_GL_CODES_PANEL = "actions/documents/hide-gl-codes-panel";
export const DEFAULT_COLUMNS = "actions/documents/default-columns";
export const SET_GRID_WIDTH = "actions/documents/set-grid-width";
export const REFRESH_DOCUMENTS = "actions/documents/refresh-documents";
export const SET_REFRESH_INTERVALS = "actions/documents/set-refresh-intervals";
export const CLEAR_REFRESH_INTERVALS = "actions/documents/clear-refresh-intervals";
export const START_EXPORT_MODE = "actions/documents/start-export-mode";
export const END_EXPORT_MODE = "actions/documents/end-export-mode";
export const SHIFT_PRESSED = "actions/documents/shift-pressed";
export const SHIFT_RELEASED = "actions/documents/shift-released";

export const fetchDocumentsAction = (
  accountId,
  filters,
  pagination,
  showLoading = true,
  resetPageNumber = false
) => (dispatch) => {
  const updatedPagination = resetPageNumber ? R.set(R.lensProp("pageNumber"), 1, pagination) : pagination;
  if (showLoading) {
    dispatch({ type: LOADING_START });
  }
  fetchDocuments(accountId, filters, updatedPagination)
    .then(({ documents, totalCount, totalCountFiltered, totalSumFiltered }) => {
      const filtersCount = filterCounter(filters);
      dispatch({
        type: SET_DOCUMENTS,
        data: {
          documents,
          filtersCount,
          totalCount,
          totalCountFiltered,
          resetPageNumber,
          totalSumFiltered
        }
      });
      /* This is a temporary solution to solve an issue where the width of the data grid (react-data-grid) is
         incorrect when a small number of rows are on display */
      window.dispatchEvent(new Event("resize"));
    });
};

export const setPaginationLimitAction = (limit) =>
  filterActionGenerator(SET_PAGINATION_LIMIT, reloadDocuments, limit);

export const toggleColumnAction = (key) => (dispatch) => {
  dispatch({
    type: TOGGLE_COLUMN,
    data: {
      key
    }
  });
  const columns = R.path(["documents", "columns"], store.getState());
  const visibleColumns = columns.filter(col => col.visible === true);
  const keys = visibleColumns.map(col => col.key);
  storage.set(VISIBLE_DOCUMENTS_COLUMNS, JSON.stringify(keys));
  window.dispatchEvent(new Event("resize"));
};

export const reloadDocuments = (dispatch, resetPageNumber = true) => {
  setTimeout(() => {
    const state = store.getState();
    const filters = state.documentFilters;
    const account = state.app.selectedAccount;
    const pagination = state.documents.pagination;
    dispatch(fetchDocumentsAction(account.id, filters, pagination, false, resetPageNumber));
  }, 0);
};

export const nextPageAction = () =>
  paginationActionGenerator(NEXT_PAGE, reloadDocuments);

export const previousPageAction = () =>
  paginationActionGenerator(PREVIOUS_PAGE, reloadDocuments);

export const setEditColumnsVisibilityAction = (visible) => ({
  type: SET_EDIT_COLUMNS_VISIBILITY,
  data: { visible }
});

export const selectDocumentsAction = (documentIds) => ({
  type: SELECT_DOCUMENTS,
  data: { documentIds }
});

export const deselectDocumentsAction = (documentIds) => ({
  type: DESELECT_DOCUMENTS,
  data: { documentIds }
});

export const startBatchEditModeAction = () => ({
  type: START_BATCH_EDIT_MODE
});

export const endBatchEditModeAction = () => ({
  type: END_BATCH_EDIT_MODE
});

export const submitBatchEditAction = () => (
  submitBatchEditActionGenerator(
    BATCH_EDIT_LOADING,
    BATCH_EDIT_LOADING_STOP,
    reloadDocuments,
    "documentsBatchEdit",
    "documents"
  )
);

export const submitBatchAssignCategoriesAction = () => (dispatch) => {
  dispatch({
    type: BATCH_ASSIGN_CATEGORIES_LOADING
  });

  const state = store.getState();
  const account = state.app.selectedAccount;
  const selectedCategories = R.pathOr([], ["form", "batchCategoriesForm", "values", "selectedCategories"], state);
  const documentIds = R.path(["documents", "selectedDocumentIds"], state);
  const documents = R.path(["documents", "documents"], state);
  const selectedDocuments = documentIds.map(id => documents.find(document => document.id === id));
  return batchAssignCategories(account.id, selectedDocuments, selectedCategories)
    .then(() => reloadDocuments(dispatch, false))
    .then(() => {
      dispatch({ type: BATCH_ASSIGN_CATEGORIES_LOADING_STOP });
      dispatch(trackEventAction("Batch assign categories"));
    })
    .catch(() => dispatch({ type: BATCH_ASSIGN_CATEGORIES_LOADING_STOP }));
};

export const startBatchCategoriesModeAction = () => ({
  type: START_BATCH_CATEGORIES_MODE
});

export const endBatchCategoriesModeAction = () => ({
  type: END_BATCH_CATEGORIES_MODE
});

export const startBatchDownloadModeAction = () => ({
  type: START_BATCH_DOWNLOAD_MODE
});

export const endBatchDownloadModeAction = () => ({
  type: END_BATCH_DOWNLOAD_MODE
});

export const submitBatchDownloadDocumentsAction = () => (dispatch) => {
  const state = store.getState();
  const account = state.app.selectedAccount;
  const formData = R.path(["form", "batchDownloadDocumentsForm", "values"], state);
  const documentIds = R.path(["documents", "selectedDocumentIds"], state);
  const emails = formData.emails;
  const exportType = formData.exportType;
  const data = { account, formData, documentIds, emails, exportType };
  return batchDownloadsSharedAction(dispatch, data, startBatchDownloadModeAction, endBatchDownloadModeAction)
    .then(() => {
      dispatch(endHamburgerModeAction());
      dispatch(trackEventAction("Batch download documents"));
    });
};

export const setTrashDocumentVisibilityAction = (visible) => ({
  type: SET_TRASH_DOCUMENT_VISIBILITY,
  data: { visible }
});

export const setChangeDocumentTypeVisibilityAction = (visible) => ({
  type: SET_CHANGE_DOCUMENT_TYPE_VISIBILITY,
  data: { visible }
});

export const setShareUrlVisibilityAction = (visible) => ({
  type: SET_SHARE_URL_VISIBILITY,
  data: { visible }
});

export const documentViewedAction = (data) =>
  documentViewedActionGenerator(DOCUMENT_VIEWED, data);

export const mergeDocumentsAction = () => (dispatch) => {
  dispatch({
    type: MERGE_DOCUMENTS_LOADING
  });
  const state = store.getState();
  const account = state.app.selectedAccount;
  const documentIds = R.path(["documents", "selectedDocumentIds"], state);
  const documents = R.path(["documents", "documents"], state);
  const selectedDocuments = documentIds.map(id => documents.find(document => document.id === id));
  const formData = new FormData();
  const newDocument = findDefaultMergeDocument("documents", selectedDocuments, state);
  newDocument.notes = `${newDocument.notes} \n Merged from documents:`;
  const ajaxPromises = selectedDocuments.map(doc => putDocument(account.id, doc.id, R.assoc("trashed", true, doc)));
  selectedDocuments.forEach(document => {
    const date = moment(document.issued).format("DD/MM/YY");
    formData.append("attachment", document.attachment.url);
    newDocument.notes = `${newDocument.notes} \n - Vendor: ${document.vendor}, Date: ${date}`;
  });
  formData.append("document", JSON.stringify(newDocument));
  return mergeDocuments(account.id, formData)
    .then(() => Promise.all(ajaxPromises)
      .then(reloadDocuments(dispatch, false))
      .then(() => {
        dispatch({ type: MERGE_DOCUMENTS_LOADING_STOP });
        dispatch(endMergeDocumentsModeAction());
        dispatch(endHamburgerModeAction());
        dispatch(trackEventAction("Merge documents"));
      })
      .catch(() => dispatch({ type: MERGE_DOCUMENTS_LOADING_STOP })))
    .catch(() => dispatch({ type: MERGE_DOCUMENTS_LOADING_STOP }));
};

export const findDefaultMergeDocument = (documentType, documents, state) => {
  const mergeDocument = R.path([documentType, "mergeDocument"], state);
  const mergeDocumentId = R.path([documentType, "selectedMergeDocumentId"], state);
  let defaultMergeDocument = documents[0];
  if (mergeDocumentId !== "") {
    defaultMergeDocument = documents.find((document) => document.id === mergeDocumentId);
  }
  if (mergeDocument !== null) {
    defaultMergeDocument = R.merge(defaultMergeDocument, R.map(((x) => x.data), mergeDocument));
  }
  return defaultMergeDocument;
};

export const setTrashMultipleDocumentsVisibilityAction = (visible) => ({
  type: SET_TRASH_MULTIPLE_DOCUMENTS_VISIBILITY,
  data: { visible }
});

export const trashMultipleDocumentsAction = () => (dispatch) => {
  dispatch({ type: TRASH_MULTIPLE_DOCUMENTS_LOADING });

  const state = store.getState();
  const account = state.app.selectedAccount;
  const documentIds = R.path(["documents", "selectedDocumentIds"], state);
  const documents = R.path(["documents", "documents"], state);
  const selectedDocuments = documentIds.map(id => documents.find(document => document.id === id));
  return trashStatusMultipleDocuments(account.id, selectedDocuments, true)
    .then(() => reloadDocuments(dispatch, false))
    .then(() => {
      dispatch({ type: TRASH_MULTIPLE_DOCUMENTS_LOADING_STOP });
      dispatch(trackEventAction("Trash multiple documents"));
    })
    .catch(() => dispatch({ type: TRASH_MULTIPLE_DOCUMENTS_LOADING_STOP }));
};

export const startMergeDocumentsModeAction = (documents, mergeDocumentId) => {
  const mergeDocument = documents.find(document => document.id === mergeDocumentId);
  return ({
    type: START_MERGE_DOCUMENTS_MODE,
    data: { mergeDocument }
  });
};

export const endMergeDocumentsModeAction = () => ({
  type: END_MERGE_DOCUMENTS_MODE
});

export const startHamburgerModeAction = () => ({
  type: START_HAMBURGER_MODE
});

export const endHamburgerModeAction = () => ({
  type: END_HAMBURGER_MODE
});

export const startDocViewerHamburgerModeAction = () => ({
  type: START_DOC_VIEWER_HAMBURGER_MODE
});

export const endDocViewerHamburgerModeAction = () => ({
  type: END_DOC_VIEWER_HAMBURGER_MODE
});

export const startDraggabilityAction = () => {
  setTimeout(() => {
    window.dispatchEvent(new Event("resize"));
  }, 0);
  return ({ type: START_DRAGGABILITY });
};

export const endDraggabilityAction = () => {
  setTimeout(() => {
    window.dispatchEvent(new Event("resize"));
  }, 0);
  return ({ type: END_DRAGGABILITY });
};

export const startFiltersDrawerModeAction = () => ({
  type: START_FILTERS_DRAWER_MODE
});

export const endFiltersDrawerModeAction = () => ({
  type: END_FILTERS_DRAWER_MODE
});

export const restoreDocumentsAction = () => (dispatch) => {
  dispatch({ type: RESTORE_DOCUMENTS_LOADING });
  const state = store.getState();
  const account = state.app.selectedAccount;
  const documentIds = R.path(["documents", "selectedDocumentIds"], state);
  const documents = R.path(["documents", "documents"], state);
  const selectedDocuments = documentIds.map(id => documents.find(document => document.id === id));
  return trashStatusMultipleDocuments(account.id, selectedDocuments, false)
    .then(() => reloadDocuments(dispatch, false))
    .then(() => {
      dispatch({ type: RESTORE_DOCUMENTS_LOADING_STOP });
      dispatch(setFlashMessageAction("Documents restored"));
      dispatch(trackEventAction("Restore documents from trash"));
    })
    .catch(() => dispatch({ type: RESTORE_DOCUMENTS_LOADING_STOP }));
};

export const deletePermanentlyAction = () => (dispatch) => {
  dispatch({ type: DELETE_DOCUMENTS_PERMANENTLY_LOADING });
  const state = store.getState();
  const account = state.app.selectedAccount;
  const documentIds = R.path(["documents", "selectedDocumentIds"], state);
  const documents = R.path(["documents", "documents"], state);
  const selectedDocuments = documentIds.map(id => documents.find(document => document.id === id));
  return deleteDocumentsPermanently(account.id, selectedDocuments)
    .then(() => reloadDocuments(dispatch, false))
    .then(() => {
      dispatch({ type: DELETE_DOCUMENTS_PERMANENTLY_LOADING_STOP });
      dispatch(setDeletePermanentlyVisibilityAction(false));
      dispatch(setFlashMessageAction("Documents deleted"));
      dispatch(trackEventAction("Delete documents permanently"));
    })
    .catch(() => dispatch({ type: DELETE_DOCUMENTS_PERMANENTLY_LOADING_STOP }));
};

export const setDeletePermanentlyVisibilityAction = (visible) => ({
  type: SET_DELETE_PERMANENTLY_VISIBILITY,
  data: { visible }
});

export const selectMergeDocumentAction = (id, row) => ({
  type: SELECT_MERGE_DOCUMENT,
  data: { id, row }
});

export const deselectMergeDocumentAction = () => ({
  type: DESELECT_MERGE_DOCUMENT
});

export const selectMergeFieldAction = (field, data, id) => ({
  type: SELECT_MERGE_FIELD,
  data: { field, data, id }
});

export const deselectAllDocumentsAction = () => ({
  type: DESELECT_ALL_DOCUMENTS
});

export const setDocumentViewerVisibilityAction = (visible) => {
  setTimeout(() => {
    window.dispatchEvent(new Event("resize"));
  }, 0);
  return ({
    type: SET_DOCUMENT_VIEWER_VISIBILITY,
    data: { visible }
  });
};

export const selectAllDocumentsAction = (accountId, filters) => (dispatch) => (
  selectAllDocuments(accountId, filters)
    .then(({ documentIds, documents }) => {
      dispatch({
        type: SELECT_ALL_DOCUMENTS,
        data: { documentIds, documents }
      });
      dispatch(trackEventAction("selectAllDocuments"));
    })
);

export const generateShareUrlAction = (accountId, documentId) => (dispatch) => (
  getShareUrl(accountId, documentId)
    .then(({ shareUrl }) => {
      dispatch({
        type: SHARE_URL_GENERATED,
        data: { shareUrl }
      });
    })
);

export const mapVendorRulesAction = (accountId, documents) => (dispatch) => (
  fetchVendorRules(accountId, documents)
  .then(({ rules }) => {
    const newDocuments = documents.map((document, index) => {
      const newDocument = R.assoc("rules", rules[index] || [], document);
      if (document.categories === undefined) {
        newDocument.categories = [];
        return newDocument;
      }
      return newDocument;
    });
    dispatch({
      type: SET_DOCUMENT_RULES,
      data: { newDocuments }
    });
  })
);

export const showGLCodesPanelAction = () => ({
  type: SHOW_GL_CODES_PANEL
});

export const hideGLCodesPanelAction = () => ({
  type: HIDE_GL_CODES_PANEL
});

export const returnToDefaultColumnsAction = () => (dispatch) => {
  dispatch({ type: DEFAULT_COLUMNS });
  const columns = columnObjects.filter(col => col.visible === true);
  const keys = columns.map(col => col.key);
  window.dispatchEvent(new Event("resize"));
  return storage.set(VISIBLE_DOCUMENTS_COLUMNS, JSON.stringify(keys));
};

export const setGridWidthAction = (width) => ({
  type: SET_GRID_WIDTH,
  data: { width }
});

export const startRefreshIntervalsAction = () => (dispatch) => {
  const refreshInterval = setInterval(() => {
    const state = store.getState();
    const filters = state.documentFilters;
    const account = state.app.selectedAccount;
    const pagination = state.documents.pagination;
    return fetchDocumentsForRefresh(account.id, filters, pagination, dispatch);
  }, 37000);
  const exportRefreshInterval = setInterval(() => {
    const state = store.getState();
    const documents = state.documents.documents;
    const isExporting = documents.some((document) => (
      document.categories ? document.categories.indexOf("Queued for Export") > -1 : false
    ));
    if (isExporting) {
      const filters = state.documentFilters;
      const account = state.app.selectedAccount;
      const pagination = state.documents.pagination;
      return fetchDocumentsForRefresh(account.id, filters, pagination, dispatch);
    }
    return clearInterval(exportRefreshInterval);
  }, 7000);

  return dispatch({
    type: SET_REFRESH_INTERVALS,
    data: { intervalIds: [refreshInterval, exportRefreshInterval] }
  });
};

const fetchDocumentsForRefresh = (accountId, filters, pagination, dispatch) => (
  fetchDocuments(accountId, filters, pagination)
    .then(({ documents }) => {
      dispatch({
        type: REFRESH_DOCUMENTS,
        data: { documents }
      });
    })
);

export const clearRefreshIntervalsAction = () => (dispatch) => {
  const state = store.getState();
  const intervalIds = state.documents.intervalIds;
  dispatch({ type: CLEAR_REFRESH_INTERVALS });
  return intervalIds.forEach((interval) => clearInterval(interval));
};

export const startExportModeAction = () => ({
  type: START_EXPORT_MODE
});

export const endExportModeAction = () => ({
  type: END_EXPORT_MODE
});

export const shiftPressedAction = (e) => {
  if (isShiftPressed(e)) {
    return ({
      type: SHIFT_PRESSED
    });
  }
  return ({ type: "NOOP" });
};

export const shiftReleasedAction = (e) => {
  if (isShiftPressed(e)) {
    return ({
      type: SHIFT_RELEASED
    });
  }
  return ({ type: "NOOP" });
};



// WEBPACK FOOTER //
// ./src/js/actions/documents-actions.js